<script setup lang="ts">
import { NewFleet, type Fleet } from '@/lib/trashsim/fleet';
import type { Party, PartyKey } from '@/lib/trashsim/party';
import { computed, ref } from 'vue';
import SimulateInput from '@/components/SimulateInput.vue';
import type { CalculationResult } from '@/lib/calculator/result';
import type { Settings } from '@/lib/trashsim/settings';
import { LoadAPI } from '@/lib/trashsim/loaders/api';
import { AllianceClass, CharacterClass } from '@/lib/trashsim/classes';
import { CombatTechs, DriveTechs, defaultDefenses, defaultMissiles, defaultResources, defaultShip, defaultShips } from '@/lib/trashsim/types';
import Format from '@/lib/trashsim/format';
import { GetEntitiesFromVersion } from '@/lib/entityInfo';
import { useCapacity, useDistance, useDuration } from '@/lib/trashsim/flight';

const props = defineProps<{
    modelValue: Party
    defender: Fleet
    settings: Settings
    title: PartyKey
    result: CalculationResult | null | undefined
}>()

const emits = defineEmits<{
    (e: 'update:modelValue', value: Party): void
    (e: 'submit'): void
    (e: 'loadSettings', value?: Settings): void
}>()

const getSpeeds = (characterClass: CharacterClass): number[] => {
    if (characterClass == CharacterClass.General) {
        return [100, 95, 90, 85, 80, 75, 70, 65, 60, 55, 50, 45, 40, 35, 30, 25, 20, 15, 10, 5]
    }
    return [100, 90, 80, 70, 60, 50, 40, 30, 20, 10]
}

// party
const party = computed({
    get: () => props.modelValue,
    set: (value: Party) => emits('update:modelValue', value)
})
const settings = computed(() => props.settings)

// fleet handlers
const selectedFleet = ref(0)
const activeFleet = computed({
    get: () => party.value.fleets[selectedFleet.value],
    set: (value: Fleet) => selectedFleet.value = party.value.fleets.findIndex(fleet => fleet.ID == value.ID)
})
const activeFleetIndex = computed(() => party.value.fleets.findIndex(fleet => fleet.ID == activeFleet.value.ID))

const addFleet = () => {
    party.value.fleets.push(NewFleet(false))
    selectedFleet.value = party.value.fleets.length - 1
}

const removeActiveFleet = () => {
    const index = activeFleetIndex.value
    if (party.value.fleets.length > 0 && index >= 0) {
        party.value.fleets.splice(index, 1)
    }

    activeFleet.value = party.value.fleets[index == party.value.fleets.length ? party.value.fleets.length -1 : index]
}

const clearPlayer = () => {
    const index = party.value.fleets.findIndex(fleet => fleet.ID == activeFleet.value.ID)
    party.value.fleets[index] = NewFleet(activeFleet.value.isDefender)
    activeFleet.value = party.value.fleets[index]
}

const clearFleet = () => {
    activeFleet.value.ships = defaultShips()

    if (activeFleet.value.isDefender) {
        activeFleet.value.resources = defaultResources()
        activeFleet.value.defenses = defaultDefenses()
        activeFleet.value.missiles = defaultMissiles()
        activeFleet.value.ships['212'] = defaultShip()
        activeFleet.value.ships['217'] = defaultShip()
    }
}

// api loading
const loadAPI = () => {
    if (activeFleet.value.API == null || activeFleet.value.API === undefined) {
        return
    }

    LoadAPI(activeFleet.value.API, activeFleet.value.isDefender).then(({fleet, settings}) => {
        party.value.fleets[activeFleetIndex.value] = fleet
        emits('loadSettings', settings)
    }).catch(err => {
        console.error('load api failed with', err)
    })
}

const submit = async () => emits('submit')

// fuel and cargo helpers
// const flight = new Flight(activeFleet, GetEntitiesFromVersion(props.settings.version), props.settings)

const entities = computed(() => GetEntitiesFromVersion(props.settings.version))
const capacity = useCapacity(activeFleet, entities, props.settings)
const distance = useDistance(activeFleet, props.defender, props.settings)
const duration = useDuration(activeFleet, distance, props.title == 'defenders', entities, props.settings)

</script>

<template>
    <h3>
    {{ $t('home.party.'+title) }}
    <span :id="'live-retreater-'+title" class="live-retreat">{{ Format.Dash(null) }}</span>
    </h3>
    <div class="party-selection">
        <ul class="list clearfix">
            <li 
                v-for="(fleet, index) in party.fleets"
                v-bind:class="{selected: activeFleet == fleet}"
                @click="activeFleet = fleet"
            >{{ title.charAt(0).toUpperCase() }}{{ index+1 }}</li>
            <li class="add-player" @click="addFleet">+</li>
        </ul>
    </div>
    <div class="content">
        <div class="remove-player btn-close"
            v-if="party.fleets.length > 1"
            @click="removeActiveFleet"
        >x</div>
        <div class="party-api-wrapper">
            <label class="clearfix">
                {{ $t('home.player.api.label') }}: 
                <input class="input-sr-key" type="text" v-model="activeFleet.API"/>
            </label>
        </div>
        <div class="party-api-load btn btn-light" @click="loadAPI">{{ $t('home.player.api.load') }}</div>

        <div class="player-classes-wrapper">
            <div class="player-classes">
                <h4>{{ $t('home.player.class.alliance') }}</h4>
                <ul class="player-class-selection alliance">
                    <li v-for="allianceClass in [AllianceClass.Traders, AllianceClass.Warriors, AllianceClass.Researchers]"
                        :class="allianceClass"
                        v-bind:class="{selected: activeFleet.allianceClass == allianceClass}"
                    >
                        <SimulateInput 
                            :label="$t('home.player.class.'+allianceClass)" v-model="activeFleet.allianceClass"
                            type="radio" :value="allianceClass" :none-value="AllianceClass.None"
                        />
                    </li>
                </ul>
            </div>
            <div class="player-classes">
                <h4>{{ $t('home.player.class.character') }}</h4>
                <ul class="player-class-selection character">
                    <li v-for="characterClass in [CharacterClass.Collector, CharacterClass.General, CharacterClass.Discoverer]"
                        :class="characterClass"
                        v-bind:class="{selected: activeFleet.characterClass == characterClass}"
                    >
                        <SimulateInput 
                            :label="$t('home.player.class.'+characterClass)" v-model="activeFleet.characterClass"
                            type="radio" :value="characterClass" :none-value="CharacterClass.None"
                        />
                    </li>
                </ul>
            </div>
        </div>
        <div class="player-techs-wrapper">
            <!-- Combat -->
            <div class="player-techs">
                <h4>{{ $t('home.player.techs.combat') }}</h4>
                <ul class="simulate-values">
                    <li class="clearfix" v-for="tech in CombatTechs">
                        <SimulateInput
                            v-model="activeFleet.techs[tech]"
                            :label="$t('home.player.techs.'+tech.toString())"
                            type="number" @submit="submit"
                        />
                    </li>
                </ul>
            </div>

            <!-- Drives -->
            <div class="player-techs" v-if="!activeFleet.isDefender">
                <h4>{{ $t('home.player.techs.drives') }}</h4>
                <ul class="simulate-values">
                    <li class="clearfix" v-for="tech in DriveTechs">
                        <SimulateInput
                            v-model.number="activeFleet.techs[tech]"
                            :label="$t('home.player.techs.'+tech)"
                            type="number"
                            @submit="submit"
                        />
                    </li>
                </ul>
            </div>

            <!-- Hyperspacetech -->
            <div class="player-techs player-techs-hyper">
                <ul class="simulate-values">
                    <li class="clearfix">
                        <SimulateInput
                            v-model="activeFleet.techs['114']"
                            :label="$t('home.player.techs.hyperspacetech')"
                            type="number"
                            @submit="submit"
                        />
                    </li>
                </ul>
            </div>
        </div>

        <h4>{{ $t('home.player.ships') }}</h4>
        <ul class="simulate-values">
            <li class="clearfix" v-for="(_, entity) in defaultShips()">
                <SimulateInput
                    v-model="activeFleet.ships[entity].amount"
                    :booster="activeFleet.ships[entity]"
                    :label="$t('type.'+entity)"
                    :result="result != null && activeFleet.ships[entity] ? result?.entitiesRemaining[title][activeFleetIndex][entity] || 0 : null"
                    type="number"
                    @submit="submit"
                />
            </li>
        </ul>
        <div class="player-coordinates">
            <h4 class="clearfix">
                {{ $t('home.player.coords') }}:
                <div>
                    <input type="number" v-model="activeFleet.coordinates.galaxy" min="1" :max="settings.numberOfGalaxies" />
                    : 
                    <input type="number" v-model="activeFleet.coordinates.system" min="1" max="499" />
                    : 
                    <input type="number" v-model="activeFleet.coordinates.position" min="1" max="15" />
                </div>
            </h4>
            <h4 class="clearfix">
                {{ $t('home.player.speed') }}
                <div v-if="!activeFleet.isDefender">
                    <select v-model="activeFleet.speed">
                        <option
                            v-for="speed in getSpeeds(activeFleet.characterClass)"
                            :value="speed"
                        >{{ speed }}%</option>
                    </select>
                </div>
                <div v-else>
                    <select disabled>
                        <option value="-">-</option>
                    </select>
                </div>
            </h4>
            <div class="clearfix player-flight-data">
                {{ $t('home.player.flighttime.label') }}
                <!-- FIXME add duration -->
                <span>{{ Format.Dash(Format.Duration(duration)) }}</span>
            </div>
            <div class="clearfix player-flight-data">
                {{ $t('home.player.consumption') }}
                <!-- FIXME add fuel -->
                <span>{{ Format.Number(0, '-') }}</span>
            </div>
            <div class="clearfix player-flight-data">
                {{ $t('home.player.cargocapacity') }}
                <span>{{ Format.Number(activeFleet.isDefender ? null : capacity, '-') }}</span>
            </div>
            <div class="player-bottom-actions">
                <div class="clear-player btn btn-light" @click="clearPlayer()" >{{ $t('home.player.clear.player') }}</div>
                <div class="clear-player btn btn-light" @click="clearFleet()" >{{ $t('home.player.clear.entities') }}</div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.player-class-selection {
    --icon-size: 40px;
}
.player-classes-wrapper {
    display: flex;
    flex-flow: wrap row;
}

.player-class-selection {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
}
.player-class-selection li {
    width: var(--icon-size);
    display: inline-block;
    /* border-radius: 50%; */
}
.player-class-selection li + li {
    margin-left: 5px;
}

.player-class-selection li:deep(input) {
    display: none;
}

.player-class-selection li:deep(label) {
    display: block;
    cursor: pointer;
}
.player-class-selection li:deep(label span) {
    display: block;
    padding-top: 100%;
    height: 0;
    text-indent: -9999px;
    filter: grayscale(1);
}
.player-class-selection li.selected:deep(label span) {
    filter: grayscale(0);
}
.player-class-selection li:deep(label span) {
    background-size: var(--icon-size);
    transition: all 300ms ease-in-out;
}

.player-class-selection.alliance li.warriors:deep(label span) {
    background-image: url(@/assets/images/warrior.png);
}
.player-class-selection.alliance li.traders:deep(label span) {
    background-image: url(@/assets/images/trader.png);
}
.player-class-selection.alliance li.researchers:deep(label span) {
    background-image: url(@/assets/images/researcher.png);
}

.player-class-selection.character li.collector:deep(label span) {
    background-image: url(@/assets/images/collector.png);
}
.player-class-selection.character li.general:deep(label span) {
    background-image: url(@/assets/images/general.png);
}
.player-class-selection.character li.discoverer:deep(label span) {
    background-image: url(@/assets/images/discoverer.png);
}


.player-classes {
    flex: 1 0 48%;
    box-sizing: border-box;
}

.player-techs-wrapper {
    display: flex;
    flex-flow: wrap row;
}
.player-techs {
    flex: 1 0 48%;
    box-sizing: border-box;
}
.player-techs:nth-child(2n+0) {
    margin-left: 4%;
}
.player-techs-hyper {
    flex: 1 0 100%;
    margin: 0 !important;
}
.player-techs input.invalid { background-color: rgb(255,88,88); }
.player-techs input.warning { background-color: #fddb55; }
.player-techs .tech-bonus {
    color: rgba(255, 205, 5, 1);
}
</style>